import { useEffect, useState } from 'react';
import { ROUNDS_TO_WIN } from '@game/shared';
import { cn } from '../lib/cn.js';
import { useGameStore, type LobbyRow } from '../game/store.js';
import { Button } from './button.jsx';

/** Ticks the "back to the lobby" countdown; the deadline lives in the store. */
const useCountdown = (endsAtMs: number): number => {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (endsAtMs === 0) {
      setRemaining(0);
      return;
    }
    const update = () => setRemaining(Math.max(0, endsAtMs - performance.now()));
    update();
    const timer = window.setInterval(update, 200);
    return () => window.clearInterval(timer);
  }, [endsAtMs]);
  return remaining;
};

const TeamColumn = ({ team, rows }: { team: 0 | 1; rows: LobbyRow[] }) => (
  <div>
    <div
      className={cn(
        'text-[11px] uppercase tracking-[0.2em]',
        team === 0 ? 'text-team-blue' : 'text-team-red',
      )}
    >
      {team === 0 ? 'Blue' : 'Red'}
    </div>
    <ul className="mt-2 space-y-1">
      {rows.map((row) => (
        <li key={row.id} className="flex items-center justify-between gap-3 text-sm">
          <span className={cn('truncate', row.isSelf && 'font-semibold')}>
            {row.name}
            {row.isSelf && <span className="ml-1.5 text-ink-muted">(you)</span>}
          </span>
          <span className={row.ready ? 'text-health' : 'text-ink-muted'}>
            {row.ready ? 'rematch ✓' : '…'}
          </span>
        </li>
      ))}
      {rows.length === 0 && <li className="text-sm text-ink-muted">Left the match</li>}
    </ul>
  </div>
);

/**
 * The end of a game: one team reached ROUNDS_TO_WIN. A rematch only happens if
 * *everyone* presses Rematch before the window closes — anything else walks the
 * players back to the lobby, so nobody is ever held hostage by the button.
 */
export const GameOver = ({
  onRematch,
  onLeave,
}: {
  onRematch: () => void;
  onLeave: () => void;
}) => {
  const mapName = useGameStore((state) => state.mapName);
  const roundWins = useGameStore((state) => state.roundWins);
  const teamScores = useGameStore((state) => state.teamScores);
  const lobby = useGameStore((state) => state.lobby);
  const phaseEndsAtMs = useGameStore((state) => state.phaseEndsAtMs);

  const remainingMs = useCountdown(phaseEndsAtMs);
  const winner = roundWins[0] > roundWins[1] ? 0 : 1;
  const me = lobby.find((row) => row.isSelf);
  const votes = lobby.filter((row) => row.ready).length;

  return (
    <div className="fixed inset-0 z-10 grid place-items-center bg-surface-900/75 backdrop-blur-sm">
      <div className="w-[min(560px,92vw)] animate-rise rounded-xl border border-white/10 bg-surface-800/90 p-8 text-center">
        <div className="text-[11px] uppercase tracking-[0.25em] text-ink-muted">
          {mapName} · first to {ROUNDS_TO_WIN}
        </div>
        <h2
          className={cn(
            'mt-1 text-4xl font-extrabold tracking-tight',
            winner === 0 ? 'text-team-blue' : 'text-team-red',
          )}
        >
          {winner === 0 ? 'Blue' : 'Red'} wins the game
        </h2>

        <div className="mt-5 flex items-center justify-center gap-8 tabular">
          <div>
            <div className="text-6xl font-bold text-team-blue">{roundWins[0]}</div>
            <div className="mt-1 text-[11px] tracking-[0.2em] text-ink-muted">BLUE</div>
          </div>
          <div className="text-2xl text-ink-muted">—</div>
          <div>
            <div className="text-6xl font-bold text-team-red">{roundWins[1]}</div>
            <div className="mt-1 text-[11px] tracking-[0.2em] text-ink-muted">RED</div>
          </div>
        </div>
        <p className="mt-2 text-xs text-ink-muted tabular">
          Kills {teamScores[0]} — {teamScores[1]}
        </p>

        <div className="mt-6 grid grid-cols-2 gap-8 border-t border-white/10 pt-5 text-left">
          <TeamColumn team={0} rows={lobby.filter((row) => row.team === 0)} />
          <TeamColumn team={1} rows={lobby.filter((row) => row.team === 1)} />
        </div>

        <div className="mt-6 flex gap-2.5">
          <Button size="lg" onClick={onRematch} disabled={me?.ready === true}>
            {me?.ready
              ? `Waiting for the others… (${votes}/${lobby.length})`
              : votes > 0
                ? `Rematch (${votes}/${lobby.length})`
                : 'Rematch'}
          </Button>
          <Button size="lg" variant="ghost" onClick={onLeave}>
            Leave
          </Button>
        </div>

        <p className="mt-4 text-xs text-ink-muted">
          {`A rematch starts only if everyone is in. Back to the lobby in ${Math.ceil(remainingMs / 1000)}s.`}
        </p>
      </div>
    </div>
  );
};
